import React, { useState, useEffect, useRef } from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import {
  Grid,
  InputLabel,
  MenuItem,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Box,
  TextField,
  IconButton,
  Collapse,
  FormControlLabel,
  Radio,
  RadioGroup
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Warning as WarningIcon, Security as SecurityIcon, Refresh as RefreshIcon, ExpandMore, ExpandLess, Info as InfoIcon } from "@material-ui/icons";
import { withStyles } from '@material-ui/core/styles';
import {
  StyledFormControl,
  StyledSelect
} from "../fui/FuiStyles";
import { HoverTableRow } from "../ssh/SshStyles";
import axios from "axios";
import base from "../../config";

/**
 * Key Management & Rotation Page
 * @author Auto-generated
 * @version 1.0
 * @see Used in component: AdminMainPage
 */

const styles = theme => ({
  root: {
    padding: theme.spacing(3),
  },
  statusChip: {
    fontWeight: 'normal',
  },
  activeStatus: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
  expiredStatus: {
    backgroundColor: '#f44336',
    color: 'white',
  },
  expiringSoonStatus: {
    backgroundColor: '#ff9800',
    color: 'white',
  },
  successRotationStatus: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
  failedRotationStatus: {
    backgroundColor: '#f44336',
    color: 'white',
  },
  historySection: {
    marginTop: theme.spacing(4),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    maxHeight: 400,
  },
  filterContainer: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(3),
    flexWrap: 'wrap',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  sectionTitle: {
    color: '#6a1b9a',
    fontWeight: 'bold',
  },
  warningBox: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderLeft: '4px solid #fdcb6e',
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
});

const KeyManagement = ({ classes, history }) => {
  const [selectedApplication, setSelectedApplication] = useState('');
  const [selectedApplicationName, setSelectedApplicationName] = useState('');
  const [selectedEnvironment, setSelectedEnvironment] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedApiUrl, setSelectedApiUrl] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [rotationModalOpen, setRotationModalOpen] = useState(false);
  const [rotationNotes, setRotationNotes] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterEnvironment, setFilterEnvironment] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterApplicationName, setFilterApplicationName] = useState('');
  const [filterRotationStatus, setFilterRotationStatus] = useState('');
  const [filterIamAwsAccountId, setFilterIamAwsAccountId] = useState('');
  const [filterIamEnvironment, setFilterIamEnvironment] = useState('');
  const [filterIamKeyStatus, setFilterIamKeyStatus] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [esoKeys, setEsoKeys] = useState([]);
  const [esoKeysLoading, setEsoKeysLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [tableType, setTableType] = useState('ESO');
  const [iamUsersData, setIamUsersData] = useState([]);
  const isPhoenixAndSaas = () => (selectedApplication || '').toLowerCase() === 'phoenix & saas';

  // Tracks locally updated rows so backend refresh never overwrites them
  const localOverrides = useRef({});

  const getUniqueValues = (data, field) =>
    [...new Set(data.map(item => item[field]).filter(val => val && val !== 'N/A'))].sort();

  const DetailField = ({ label, value, monospace = false, highlighted = false }) => (
    <Box style={{ marginBottom: '8px' }}>
      <Typography
        variant="overline"
        style={{
          fontSize: '0.75rem',
          fontWeight: 700,
          color: '#555',
          letterSpacing: '0.5px',
          lineHeight: 1,
          display: highlighted ? 'block' : undefined
        }}
      >
        {label}
      </Typography>
      {highlighted ? (
        <Box style={{
          backgroundColor: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '4px',
          padding: '10px 12px',
          marginTop: '6px'
        }}>
          <Typography
            variant="body2"
            style={{
              fontFamily: monospace ? 'Monaco, Consolas, "Courier New", monospace' : undefined,
              fontSize: monospace ? '0.8rem' : undefined,
              wordBreak: 'break-all',
              color: '#495057',
              lineHeight: 1.4,
              marginTop: '4px',
              fontWeight: 500
            }}
          >
            {value}
          </Typography>
        </Box>
      ) : (
        <Typography
          variant="body2"
          style={{
            marginTop: '4px',
            fontFamily: monospace ? 'Monaco, Consolas, "Courier New", monospace' : undefined,
            fontWeight: 500,
            color: '#333',
            minHeight: label === 'Notes' ? '20px' : undefined
          }}
        >
          {value || (label === 'Notes' ? 'No notes available' : value)}
        </Typography>
      )}
    </Box>
  );

  const headerCellStyle = {
    fontWeight: '600',
    color: '#6a1b9a',
    textAlign: 'center',
    padding: '8px 16px',
    fontSize: '0.875rem',
    borderBottom: '2px solid #6a1b9a',
    borderRight: '1px solid #e0e0e0'
  };

  const lastHeaderCellStyle = {
    ...headerCellStyle,
    borderRight: 'none'
  };

  const tableHeaders = [
    'Application', 'Application Name', 'Environment', 'Last Updated', 'Key Status', 'Update Status', 'Details'
  ];

  const iamTableHeaders = [
    'IAM User Name', 'AWS Account ID', 'Environment', 'Secret Name', 'Key Status', 'Rotation Type', 'Last Rotated Date', 'Details'
  ];

  const getUniqueApplications = () => {
    const filtered = getUniqueValues(esoKeys, 'Application').filter(app =>
      app.toLowerCase() !== 'phoenix' && app.toLowerCase() !== 'saas'
    );
    return ['Phoenix & SaaS', ...filtered];
  };
  const getUniqueApplicationNames = () => {
    if (isPhoenixAndSaas()) {
      return getUniqueValues(
        esoKeys.filter(key =>
          (key.Application || '').toLowerCase() === 'phoenix' ||
          (key.Application || '').toLowerCase() === 'saas'
        ),
        'ApplicationName'
      );
    }
    return getUniqueValues(
      esoKeys.filter(key => key.Application === selectedApplication),
      'ApplicationName'
    );
  };

  const getUniqueEnvironments = () => {
    if (!selectedApplication) {
      return getUniqueValues(esoKeys, 'Environment');
    }
    // Filter by both Application AND ApplicationName so only relevant environments show
    const filteredKeys = esoKeys.filter(key =>
      key.Application === selectedApplication &&
      (!selectedApplicationName || key.ApplicationName === selectedApplicationName)
    );
    return getUniqueValues(filteredKeys, 'Environment');
  };


  // Get filtered client IDs based on selected application and environment
  const getFilteredClientIds = () => {
    if (!selectedApplication || !selectedEnvironment) {
      return [];
    }

    const filteredKeys = esoKeys.filter(key =>
      key.Application === selectedApplication &&
      key.Environment === selectedEnvironment
    );

    const clientIds = [...new Set(filteredKeys.map(key => key.ClientID).filter(id => id && id !== 'N/A'))];

    return clientIds.sort();
  };

  // Get API URL for the selected combination
  const getApiUrlForSelection = () => {
    if (!selectedApplication || !selectedEnvironment || !selectedClientId) {
      return '';
    }

    const matchingKey = esoKeys.find(key =>
      key.Application === selectedApplication &&
      key.Environment === selectedEnvironment &&
      key.ClientID === selectedClientId
    );

    return matchingKey ? matchingKey.ApiUrl || '' : '';
  };

  // Map ESO keys data to history format
  const mapEsoKeysToHistory = (esoKeysData) => {
    return esoKeysData.map((key, index) => ({
      id: index + 1,
      application: key.Application || 'N/A',
      applicationName: key.ApplicationName || 'N/A',
      environment: key.Environment || 'N/A',
      clientId: key.ClientID || 'N/A',
      resourceId: key.ResourceID || 'N/A',
      apiUrl: key.ApiUrl || 'N/A',
      authUrl: key.AuthUrl || 'N/A',
      esoKeyId: key.ESOKeyId || 'N/A',
      rotatedOn: key.LastUpdatedDate || '',
      rotatedBy: key.UpdatedBy || 'N/A',
      expirationDate: key.ExpiryDate || 'N/A',
      keyStatus: key.Status || 'Unknown',
      rotationStatus: key.UpdateStatus || 'Success',
      notes: key.Notes || ''
    })).sort((a, b) => {
      if (!a.rotatedOn && !b.rotatedOn) return 0;
      if (!a.rotatedOn) return 1;
      if (!b.rotatedOn) return -1;
      return new Date(b.rotatedOn) - new Date(a.rotatedOn);
    });
  };

  function getStatusChipClass(status) {
    switch (status) {
      case 'Active': return classes.statusChip + ' ' + classes.activeStatus;
      case 'Expired': return classes.statusChip + ' ' + classes.expiredStatus;
      case 'Expiring Soon': return classes.statusChip + ' ' + classes.expiringSoonStatus;
      default: return classes.statusChip;
    }
  }

  function getRotationStatusChipClass(rotationStatus) {
    switch (rotationStatus) {
      case 'Success': return classes.statusChip + ' ' + classes.successRotationStatus;
      case 'Failed': return classes.statusChip + ' ' + classes.failedRotationStatus;
      default: return classes.statusChip;
    }
  }

  const loadIamUserKeys = async () => {
    try {
      const idToken = localStorage.getItem("token");
      if (idToken === null || idToken === undefined) return;
      const urloptions = {
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "Authorization": "Bearer " + idToken,
        }
      };
      const apiUrl = base.app.apiSite + "fui/admin/IAM-user-keys";
      const response = await axios.get(apiUrl, urloptions);
      if (response.status === 200) {
        const rawData = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data.data)
            ? response.data.data
            : [];
        const mappedData = rawData.map((item, index) => ({
          id: index + 1,
          iamUserName: item['IAM User Name'] || 'N/A',
          awsAccountId: item['AWS Account ID'] || 'N/A',
          environment: item['Environment'] || 'N/A',
          accessKeyId: item['Access Key ID'] || 'N/A',
          createdDate: item['Created Date'] || 'N/A',
          lastRotatedDate: item['Last Rotated Date'] || '',
          rotationType: item['Rotation Type'] || 'N/A',
          rotationStatus: item['Rotation Status'] || 'N/A',
          secretName: item['Secret Name'] || 'N/A',
          keyStatus: item['Key Status'] || 'N/A',
          rotatedBy: item['Rotated By'] || 'N/A',
          notes: item['Notes'] || ''
        })).sort((a, b) => {
          if (!a.lastRotatedDate && !b.lastRotatedDate) return 0;
          if (!a.lastRotatedDate) return 1;
          if (!b.lastRotatedDate) return -1;
          return new Date(b.lastRotatedDate) - new Date(a.lastRotatedDate);
        });
        setIamUsersData(mappedData);
      }
    } catch (error) {


      if (error.response && error.response.status === 401) {
        setErrorMessage('Unauthorized. Please re-login to access IAM User Keys.');
      } else if (error.response && error.response.status === 403) {
        setErrorMessage('Access denied. You do not have permission to view IAM User Keys.');
      } else if (error.response && error.response.status === 404) {
        setErrorMessage('IAM User Keys endpoint not found. Please check the API configuration.');
      } else {
        setErrorMessage('Failed to load IAM User Keys. Please try again.');
      }
    }
  };

  useEffect(() => {
    loadEsoKeys();
    loadIamUserKeys();
  }, []);

  // Clear dependent selections when Application is changed
  useEffect(() => {
    if (isPhoenixAndSaas()) {
      const names = getUniqueValues(
        esoKeys.filter(key =>
          (key.Application || '').toLowerCase() === 'phoenix' ||
          (key.Application || '').toLowerCase() === 'saas'
        ),
        'ApplicationName'
      );
      setSelectedApplicationName(names.length === 1 ? names[0] : '');
      setSelectedEnvironment('all');
    } else {
      setSelectedApplicationName('');
      setSelectedEnvironment('');
    }
    setSelectedClientId('');
    setSelectedApiUrl('');
  }, [selectedApplication, esoKeys]);

  useEffect(() => {
    if (!selectedApplicationName || !selectedApplication) return;
    if (isPhoenixAndSaas()) {
      setSelectedClientId('');
      setSelectedApiUrl('');
      return;
    }
    const availableEnvs = getUniqueValues(
      esoKeys.filter(key =>
        key.Application === selectedApplication &&
        key.ApplicationName === selectedApplicationName
      ),
      'Environment'
    );
    const prodEnv = availableEnvs.find(env => env.toLowerCase() === 'prod');
    setSelectedEnvironment(prodEnv || (availableEnvs.length === 1 ? availableEnvs[0] : ''));
    setSelectedClientId('');
    setSelectedApiUrl('');
  }, [selectedApplicationName, esoKeys]);

  useEffect(() => {
    setSelectedClientId('');
    setSelectedApiUrl('');
  }, [selectedEnvironment]);

  // Auto-populate Client ID when Application and Environment are selected
  useEffect(() => {
    if (selectedApplication && selectedEnvironment) {
      const availableClientIds = getFilteredClientIds();

      if (availableClientIds.length === 1) {
        // If only one Client ID matches, auto-select it
        setSelectedClientId(availableClientIds[0]);
      } else if (availableClientIds.length === 0) {
        // If no Client IDs match, clear selection
        setSelectedClientId('');
        setSelectedApiUrl('');
      } else if (!availableClientIds.includes(selectedClientId)) {
        // If current selection is not valid for the new combination, clear it
        setSelectedClientId('');
        setSelectedApiUrl('');
      }
    } else {
      // Clear Client ID if Application or Environment is not selected
      setSelectedClientId('');
      setSelectedApiUrl('');
    }
  }, [selectedApplication, selectedEnvironment, esoKeys]);

  // Auto-populate API URL when Client ID is selected
  useEffect(() => {
    if (selectedApplication && selectedEnvironment && selectedClientId) {
      const apiUrl = getApiUrlForSelection();
      setSelectedApiUrl(apiUrl);
    } else {
      setSelectedApiUrl('');
    }
  }, [selectedApplication, selectedEnvironment, selectedClientId, esoKeys]);


  const loadEsoKeys = async () => {
    setEsoKeysLoading(true);
    try {
      // Get authorization token
      const idToken = localStorage.getItem("token");

      if (idToken === null || idToken === undefined) {
        return;
      }

      // Prepare API call options
      const urloptions = {
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "Authorization": "Bearer " + idToken,
        }
      };
      const apiUrl = base.app.apiSite + "fui/admin/eso-keys";
      const response = await axios.get(apiUrl, urloptions);
      if (response.status === 200) {
        setEsoKeys(response.data);
        const freshMapped = mapEsoKeysToHistory(response.data);

        const mergedData = freshMapped.map(function (freshRow) {
          var overrideKey = String(freshRow.esoKeyId);
          var override = localOverrides.current[overrideKey];
          if (override) {
            var backendDate = freshRow.rotatedOn ? new Date(freshRow.rotatedOn) : null;
            var overrideDate = override.rotatedOn ? new Date(override.rotatedOn) : null;
            if (backendDate && overrideDate && backendDate >= overrideDate) {
              delete localOverrides.current[overrideKey];
              return freshRow;
            }
            return Object.assign({}, freshRow, override);
          }
          return freshRow;
        });

        setHistoryData(mergedData);
        setLastUpdated(new Date());
      }
    } catch (error) {
    } finally {
      setEsoKeysLoading(false);
    }
  };

  const handleRotateKey = async () => {
    var capturedApp = selectedApplication;
    var capturedAppName = selectedApplicationName;
    var capturedEnv = selectedEnvironment;
    var capturedClientId = selectedClientId;
    var capturedApiUrl = selectedApiUrl;
    var capturedNotes = rotationNotes;
    var capturedIsPnS = (selectedApplication || '').toLowerCase() === 'phoenix & saas';

    setLoading(true);

    try {
      // Get authorization token
      const idToken = localStorage.getItem("token");

      if (idToken === null || idToken === undefined) {
        window.location.href = "/";
        return false;
      }

      // Resolve clientId for Phoenix & SaaS
      var resolvedClientId = capturedIsPnS
        ? (function () {
            var matchedKeys = esoKeys.filter(function (key) {
              return (
                ((key.Application || '').toLowerCase() === 'phoenix' ||
                  (key.Application || '').toLowerCase() === 'saas') &&
                key.ApplicationName === capturedAppName &&
                key.ClientID && key.ClientID !== 'N/A'
              );
            });
            var prodKey = matchedKeys.find(function (key) {
              return (key.Environment || '').toLowerCase() === 'prod';
            });
            var match = prodKey || matchedKeys[0];
            return match ? match.ClientID : '';
          })()
        : capturedClientId;

      // Resolve EsoKeyId
      var matchedEsoKey = capturedIsPnS
        ? (function () {
            var matchedKeys = esoKeys.filter(function (key) {
              return (
                ((key.Application || '').toLowerCase() === 'phoenix' ||
                  (key.Application || '').toLowerCase() === 'saas') &&
                key.ApplicationName === capturedAppName &&
                key.ClientID && key.ClientID !== 'N/A'
              );
            });
            return (
              matchedKeys.find(function (key) {
                return (key.Environment || '').toLowerCase() === 'prod';
              }) || matchedKeys[0]
            );
          })()
        : esoKeys.find(function (key) {
            return (
              key.Application === capturedApp &&
              key.Environment === capturedEnv &&
              key.ClientID === capturedClientId
            );
          });

      var esoKeyId = matchedEsoKey ? matchedEsoKey.ESOKeyId || '' : '';
      var updatedBy = getCurrentUser();

      var environmentToSend = capturedIsPnS
        ? 'ProdEU,ProdNonEU,Uat/Stage,Test,Demo,PreProd'
        : (capturedEnv || '');

      var queryParams = new URLSearchParams({
        application: capturedApp,
        environment: environmentToSend,
        clientId: resolvedClientId,
        EsoKeyId: esoKeyId,
        camEnvPortal: process.env.REACT_APP_CAMENVPORTAL || '',
        notes: capturedNotes,
        updatedBy: updatedBy,
        Authorization: 'Bearer ' + idToken,
      });

      var apiUrl = base.app.apiSite + "fui/admin/update-eso-key?" + queryParams.toString();

      var response = await axios.post(apiUrl, null, {
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "Authorization": "Bearer " + idToken,
        }
      });

      if (response.status === 200 || response.status === 201) {
        var responseData = {};
        try {
          if (response.data && typeof response.data === 'object') {
            responseData = response.data;
          } else if (response.data && typeof response.data === 'string') {
            responseData = JSON.parse(response.data);
          }
        } catch (parseErr) {
          responseData = {};
        }

        var rotatedNow = new Date().toISOString();
        var newExpiry = responseData.ExpiryDate
          || responseData.expirationDate
          || new Date(new Date().setDate(new Date().getDate() + 90)).toISOString();

        var overrideValues = {
          rotatedOn: rotatedNow,
          rotatedBy: responseData.UpdatedBy || updatedBy,
          expirationDate: newExpiry,
          keyStatus: responseData.Status || 'Active',
          rotationStatus: responseData.UpdateStatus || 'Success',
          notes: capturedNotes
        };

        // Store override keyed by esoKeyId
        if (esoKeyId) {
          localOverrides.current[String(esoKeyId)] = overrideValues;
        }

        // Update table using CAPTURED variables — not state which may be reset
        setHistoryData(function (prev) {
          var matchFound = false;

          var updated = prev.map(function (row) {
            var matchById = esoKeyId && row.esoKeyId &&
              String(row.esoKeyId) === String(esoKeyId);

            var matchByClient = resolvedClientId && row.clientId &&
              String(row.clientId).toLowerCase() === String(resolvedClientId).toLowerCase();

            var matchByApp = row.application && (
              String(row.application).toLowerCase() === String(capturedApp).toLowerCase() ||
              (capturedIsPnS && (
                row.application.toLowerCase() === 'phoenix' ||
                row.application.toLowerCase() === 'saas'
              ))
            );

            if (matchById || (matchByClient && matchByApp)) {
              matchFound = true;
              return Object.assign({}, row, overrideValues);
            }
            return row;
          });

          if (!matchFound) {
            var newRow = {
              id: prev.length + 1,
              application: capturedApp,
              applicationName: capturedAppName,
              environment: capturedIsPnS
                ? 'ProdEU, ProdNonEU, Uat/Stage, Test, Demo, PreProd'
                : capturedEnv,
              clientId: resolvedClientId,
              esoKeyId: esoKeyId,
              apiUrl: capturedApiUrl,
              resourceId: matchedEsoKey ? matchedEsoKey.ResourceID || 'N/A' : 'N/A',
              authUrl: matchedEsoKey ? matchedEsoKey.AuthUrl || 'N/A' : 'N/A',
              rotatedOn: overrideValues.rotatedOn,
              rotatedBy: overrideValues.rotatedBy,
              expirationDate: overrideValues.expirationDate,
              keyStatus: overrideValues.keyStatus,
              rotationStatus: overrideValues.rotationStatus,
              notes: overrideValues.notes
            };
            updated = [newRow].concat(prev);
          }

          return updated.sort(function (a, b) {
            var hasA = a.rotatedOn && a.rotatedOn !== 'N/A' && a.rotatedOn !== '';
            var hasB = b.rotatedOn && b.rotatedOn !== 'N/A' && b.rotatedOn !== '';
            if (!hasA && !hasB) return 0;
            if (!hasA) return 1;
            if (!hasB) return -1;
            return new Date(b.rotatedOn) - new Date(a.rotatedOn);
          });
        });

        setSuccessMessage('ESO Key updated successfully!');
        setLoading(false);
        setRotationModalOpen(false);

        setTimeout(function () { loadEsoKeys(); }, 5000);
      }
    } catch (error) {
      setLoading(false);

      var errorMsg = 'ESO Key update failed. Please try again.';
      if (error.response && error.response.data && error.response.data.message) {
        errorMsg = error.response.data.message;
      } else if (error.response && error.response.status === 401) {
        errorMsg = 'Unauthorized. Please check your secret key.';
      } else if (error.response && error.response.status === 403) {
        errorMsg = 'Access denied. You do not have permission to update keys.';
      }
      setSuccessMessage('');
      setErrorMessage(errorMsg);

      var failedHistoryEntry = {
        id: historyData.length + 1,
        application: capturedApp,
        applicationName: capturedAppName,
        environment: capturedEnv,
        clientId: capturedClientId,
        apiUrl: capturedApiUrl,
        rotatedOn: new Date().toISOString(),
        rotatedBy: getCurrentUser(),
        expirationDate: 'N/A',
        keyStatus: 'Unknown',
        rotationStatus: 'Failed'
      };
      setHistoryData(function (prev) { return [failedHistoryEntry].concat(prev); });
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleRowExpansion = (rowId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowId)) {
      newExpandedRows.delete(rowId);
    } else {
      newExpandedRows.add(rowId);
    }
    setExpandedRows(newExpandedRows);
  };

  const resetRotationForm = () => {
    setSelectedApplication('');
    setSelectedApplicationName('');
    setSelectedEnvironment('');
    setSelectedClientId('');
    setSelectedApiUrl('');
    setRotationNotes('');
  };

  const clearAllFilters = () => {
    [setFilterEnvironment, setFilterStatus, setFilterApplicationName, setFilterRotationStatus,
      setFilterIamAwsAccountId, setFilterIamEnvironment, setFilterIamKeyStatus].forEach(function (setter) { return setter(''); });
    setPage(0);
  };

  const getUniqueHistoryEnvironments = () => getUniqueValues(historyData, 'environment');
  const getUniqueIamAwsAccountIds = () => getUniqueValues(iamUsersData, 'awsAccountId');
  const getUniqueIamEnvironments = () => getUniqueValues(iamUsersData, 'environment');
  const getUniqueIamKeyStatuses = () => getUniqueValues(iamUsersData, 'keyStatus');
  const getUniqueHistoryApplicationNames = () => getUniqueValues(historyData, 'applicationName');

  const formatDateTime = (dateStr) => {
    if (!dateStr || dateStr === 'N/A') return 'N/A';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return 'N/A';
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const getCurrentUser = () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.email || payload.sub || payload.username || 'current.user';
      }
    } catch (error) {}
    return 'current.user';
  };

  const filteredHistoryData = historyData.filter(item =>
    (!filterEnvironment || item.environment === filterEnvironment) &&
    (!filterStatus || item.keyStatus === filterStatus) &&
    (!filterApplicationName || item.applicationName === filterApplicationName) &&
    (!filterRotationStatus || item.rotationStatus === filterRotationStatus)
  );

  const filteredIamData = iamUsersData.filter(item =>
    (!filterIamAwsAccountId || item.awsAccountId === filterIamAwsAccountId) &&
    (!filterIamEnvironment || item.environment === filterIamEnvironment) &&
    (!filterIamKeyStatus || item.keyStatus === filterIamKeyStatus)
  );

  const paginatedData = filteredHistoryData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const showSummary = selectedApplication && selectedApplicationName && (
    isPhoenixAndSaas() || (selectedEnvironment && selectedClientId && selectedApiUrl)
  );
  const isRotateDisabled =
    !selectedApplication ||
    !selectedApplicationName ||
    !rotationNotes.trim() ||
    loading ||
    (!isPhoenixAndSaas() && (!selectedEnvironment || !selectedClientId || !selectedApiUrl));

  return (
    <div className={classes.root}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <Typography variant="h4" component="h1" gutterBottom className={classes.sectionTitle}>
            Key Management & Rotation
          </Typography>
          {lastUpdated && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8, fontSize: '0.75rem' }}>
              Last updated: {lastUpdated.toLocaleString()}
            </Typography>
          )}
        </div>

        <Button
          variant="outlined"
          startIcon={esoKeysLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={() => { loadEsoKeys(); loadIamUserKeys(); }}
          disabled={esoKeysLoading}
          style={{ borderColor: '#6a1b9a', color: '#6a1b9a', minWidth: 120 }}
        >
          {esoKeysLoading ? 'Refreshing...' : 'Refresh Data'}
        </Button>
      </div>

      {/* ESO Key Update Section */}
      <div style={{
        marginTop: 32,
        marginBottom: 48,
        padding: '24px 32px',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)',
        borderRadius: '16px',
        border: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <Typography variant="h5" component="h2" style={{ color: '#6a1b9a', fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
            <SecurityIcon style={{ fontSize: '28px' }} />
            ESO Key Update
          </Typography>
          <Typography variant="body1" style={{ color: '#666', maxWidth: '600px', lineHeight: 1.5 }}>
            Update ESO keys for applications across different environments. Configure your settings and execute the rotation process.
          </Typography>
        </div>
        <Button
          variant="contained"
          size="large"
          data-open-modal="true"
          onClick={() => setRotationModalOpen(true)}
          style={{
            background: 'linear-gradient(135deg, #6a1b9a 0%, #8e24aa 100%)',
            color: 'white',
            padding: '12px 28px',
            fontSize: '1rem',
            fontWeight: '600',
            textTransform: 'none',
            borderRadius: 24,
            boxShadow: '0 6px 25px rgba(106, 27, 154, 0.3)',
            border: 'none',
            flexShrink: 0,
            marginLeft: '24px',
          }}
        >
          Update ESO Key
        </Button>
      </div>

      {/* Rotation Configuration Modal */}
      <Dialog
        open={rotationModalOpen}
        onClose={() => { resetRotationForm(); setRotationModalOpen(false); }}
        maxWidth="lg"
        fullWidth
        disableEnforceFocus
        TransitionProps={{
          onExited: function () {
            var btn = document.querySelector('[data-open-modal]');
            if (btn) { btn.focus(); }
          }
        }}
        PaperProps={{ style: { borderRadius: 16, overflow: 'hidden' } }}
      >
        <DialogTitle style={{ background: 'linear-gradient(135deg, #6a1b9a 0%, #8e24aa 100%)', color: 'white', padding: '20px 24px' }}>
          <Typography variant="h6" component="div" style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600 }}>
            Configure ESO Key Rotation
          </Typography>
        </DialogTitle>

        <DialogContent style={{ padding: '24px' }}>
          <Typography variant="body1" style={{ color: '#666', marginBottom: 24 }}>
            Configure the application details and execute the ESO key rotation process.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <StyledFormControl variant="outlined" margin="dense">
                <InputLabel>Application</InputLabel>
                <StyledSelect
                  value={selectedApplication}
                  onChange={(e) => setSelectedApplication(e.target.value)}
                  label="Application"
                  disabled={esoKeysLoading}
                >
                  <MenuItem value=""><em>{esoKeysLoading ? 'Loading applications...' : 'Select Application'}</em></MenuItem>
                  {!esoKeysLoading && getUniqueApplications().map((app) => (
                    <MenuItem key={app} value={app}>{app}</MenuItem>
                  ))}
                </StyledSelect>
              </StyledFormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <StyledFormControl variant="outlined" margin="dense">
                <InputLabel>Application Name</InputLabel>
                <StyledSelect
                  value={selectedApplicationName}
                  onChange={(e) => setSelectedApplicationName(e.target.value)}
                  label="Application Name"
                  disabled={esoKeysLoading || !selectedApplication}
                >
                  <MenuItem value=""><em>{esoKeysLoading ? 'Loading application names...' : !selectedApplication ? 'Select Application first' : 'Select Application Name'}</em></MenuItem>
                  {!esoKeysLoading && selectedApplication && getUniqueApplicationNames().map((appName) => (
                    <MenuItem key={appName} value={appName}>{appName}</MenuItem>
                  ))}
                </StyledSelect>
              </StyledFormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <StyledFormControl variant="outlined" margin="dense">
                <InputLabel>Environment</InputLabel>
                <StyledSelect
                  value={isPhoenixAndSaas() ? 'all' : selectedEnvironment}
                  onChange={(e) => { if (!isPhoenixAndSaas()) setSelectedEnvironment(e.target.value); }}
                  label="Environment"
                  disabled={esoKeysLoading || !selectedApplication || !selectedApplicationName || isPhoenixAndSaas()}
                >
                  <MenuItem value="all" style={{ display: isPhoenixAndSaas() ? 'block' : 'none' }}>
                    ProdEU, ProdNonEU, Uat/Stage, Test, Demo, PreProd
                  </MenuItem>
                  <MenuItem value="" style={{ display: isPhoenixAndSaas() ? 'none' : 'block' }}>
                    <em>{!selectedApplication ? 'Select Application first' : !selectedApplicationName ? 'Select Application Name first' : 'Select Environment'}</em>
                  </MenuItem>
                  {getUniqueEnvironments().map((env) => (
                    <MenuItem key={env} value={env} style={{ display: isPhoenixAndSaas() ? 'none' : 'block' }}>{env}</MenuItem>
                  ))}
                </StyledSelect>
              </StyledFormControl>
            </Grid>
          </Grid>

          <div style={{ marginTop: 24 }}>
            <TextField
              fullWidth
              label="Update Notes"
              variant="outlined"
              multiline
              rows={3}
              value={rotationNotes}
              onChange={(e) => setRotationNotes(e.target.value)}
              placeholder="Add notes or comments about this key rotation (required)..."
              helperText="Please provide a reason or description for this key rotation"
              required
              InputProps={{ style: { backgroundColor: 'white' } }}
            />
          </div>

          {showSummary && (
            <div style={{ marginTop: 24, padding: 20, background: '#ffffff', borderRadius: 8, border: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle1" style={{ color: '#333', fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <SecurityIcon style={{ fontSize: '18px', color: '#6a1b9a' }} />
                Rotation Configuration Summary
              </Typography>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <Typography variant="body2" style={{ color: '#666', fontWeight: 500, minWidth: '140px' }}>Application:</Typography>
                  <Typography variant="body2" style={{ color: '#333', fontWeight: 500, textAlign: 'right', flex: 1, marginLeft: 16 }}>{selectedApplication}</Typography>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <Typography variant="body2" style={{ color: '#666', fontWeight: 500, minWidth: '140px' }}>Application Name:</Typography>
                  <Typography variant="body2" style={{ color: '#333', fontWeight: 500, textAlign: 'right', flex: 1, marginLeft: 16, wordBreak: 'break-word' }}>{selectedApplicationName}</Typography>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <Typography variant="body2" style={{ color: '#666', fontWeight: 500, minWidth: '140px' }}>Environment:</Typography>
                  <Typography variant="body2" style={{ color: '#333', fontWeight: 500, textAlign: 'right', flex: 1, marginLeft: 16 }}>
                    {isPhoenixAndSaas() ? 'ProdEU, ProdNonEU, Uat/Stage, Test, Demo, PreProd' : selectedEnvironment}
                  </Typography>
                </div>
                {isPhoenixAndSaas() ? (
                  (() => {
                    const matchedKeys = esoKeys.filter(key =>
                      ((key.Application || '').toLowerCase() === 'phoenix' || (key.Application || '').toLowerCase() === 'saas') &&
                      key.ApplicationName === selectedApplicationName &&
                      key.ClientID && key.ClientID !== 'N/A'
                    );
                    const prodKey = matchedKeys.find(key => (key.Environment || '').toLowerCase() === 'prod');
                    const uniqueClientId = prodKey ? prodKey.ClientID : matchedKeys.length > 0 ? matchedKeys[0].ClientID : null;
                    return (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <Typography variant="body2" style={{ color: '#666', fontWeight: 500, minWidth: '140px' }}>Client ID:</Typography>
                        <Typography variant="body2" style={{ color: uniqueClientId ? '#333' : '#999', fontWeight: 500, textAlign: 'right', flex: 1, marginLeft: 16, fontFamily: 'Monaco, Consolas, monospace', fontSize: '0.85rem' }}>
                          {uniqueClientId || 'N/A'}
                        </Typography>
                      </div>
                    );
                  })()
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <Typography variant="body2" style={{ color: '#666', fontWeight: 500, minWidth: '140px' }}>Client ID:</Typography>
                    <Typography variant="body2" style={{ color: selectedClientId ? '#333' : '#999', fontWeight: 500, textAlign: 'right', flex: 1, marginLeft: 16, fontFamily: 'Monaco, Consolas, monospace', fontSize: '0.85rem' }}>
                      {selectedClientId || 'N/A'}
                    </Typography>
                  </div>
                )}
                {selectedApiUrl && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <Typography variant="body2" style={{ color: '#666', fontWeight: 500, minWidth: '140px' }}>API URL:</Typography>
                    <Typography variant="body2" style={{ color: '#333', fontWeight: 500, textAlign: 'right', flex: 1, marginLeft: 16, fontFamily: 'Monaco, Consolas, monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>{selectedApiUrl}</Typography>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: rotationNotes ? '1px solid #f0f0f0' : 'none' }}>
                  <Typography variant="body2" style={{ color: '#666', fontWeight: 500, minWidth: '140px' }}>Last Updated:</Typography>
                  <Typography variant="body2" style={{ color: '#333', fontWeight: 500, textAlign: 'right', flex: 1, marginLeft: 16 }}>
                    {(() => {
                      let matchingRotation;
                      if (isPhoenixAndSaas()) {
                        const matchedKeys = esoKeys.filter(key =>
                          ((key.Application || '').toLowerCase() === 'phoenix' || (key.Application || '').toLowerCase() === 'saas') &&
                          key.ApplicationName === selectedApplicationName &&
                          key.ClientID && key.ClientID !== 'N/A'
                        );
                        matchingRotation = matchedKeys.find(key => (key.Environment || '').toLowerCase() === 'prod') || matchedKeys[0];
                      } else {
                        if (!selectedClientId) return 'Never rotated';
                        matchingRotation = esoKeys.find(key =>
                          key.Application === selectedApplication &&
                          key.Environment === selectedEnvironment &&
                          key.ClientID === selectedClientId
                        );
                      }
                      return matchingRotation && matchingRotation.LastUpdatedDate
                        ? formatDateTime(matchingRotation.LastUpdatedDate)
                        : 'Never rotated';
                    })()}
                  </Typography>
                </div>
                {rotationNotes && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '6px 0' }}>
                    <Typography variant="body2" style={{ color: '#666', fontWeight: 500, minWidth: '140px' }}>Notes:</Typography>
                    <Typography variant="body2" style={{ color: '#333', fontWeight: 500, textAlign: 'right', flex: 1, marginLeft: 16, wordBreak: 'break-word' }}>{rotationNotes}</Typography>
                  </div>
                )}
              </div>

              <div style={{ padding: 12, backgroundColor: '#fff3cd', borderRadius: 6, border: '1px solid #ffeaa7', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <WarningIcon style={{ color: '#f39c12', fontSize: 16, marginTop: 1 }} />
                <Typography variant="caption" style={{ color: '#856404', fontSize: '0.8rem', lineHeight: 1.4 }}>
                  {isPhoenixAndSaas()
                    ? 'This will invalidate ESO keys across all environments (UAT, Stage, Test, Demo, PreProd & Prod) for the selected application. Ensure all environments and dependent systems are updated with the new keys before proceeding.'
                    : 'This action will immediately invalidate the current ESO key and generate a new one. Please ensure all dependent systems are prepared for the key rotation.'
                  }
                </Typography>
              </div>
            </div>
          )}
        </DialogContent>

        <DialogActions style={{ padding: '16px 24px', background: '#f8f9fa' }}>
          <Button onClick={() => { resetRotationForm(); setRotationModalOpen(false); }} style={{ color: '#666', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => { handleRotateKey(); }}
            disabled={isRotateDisabled}
            style={{
              background: isRotateDisabled ? '#e0e0e0' : 'linear-gradient(135deg, #6a1b9a 0%, #8e24aa 100%)',
              color: isRotateDisabled ? '#9e9e9e' : 'white',
              textTransform: 'none',
              padding: '10px 24px',
              borderRadius: 20,
              fontWeight: 600
            }}
          >
            {loading ? (
              <React.Fragment>
                <CircularProgress size={16} style={{ marginRight: 8, color: 'inherit' }} />
                Rotating...
              </React.Fragment>
            ) : (
              <React.Fragment>
                <SecurityIcon style={{ marginRight: 8, fontSize: '16px' }} />
                Update Key
              </React.Fragment>
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Key Rotation History Section */}
      <div className={classes.historySection}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Typography variant="h5" component="h2">Key Update History</Typography>
          <Button
            variant="outlined"
            onClick={clearAllFilters}
            disabled={!filterEnvironment && !filterStatus && !filterApplicationName && !filterRotationStatus && !filterIamAwsAccountId && !filterIamEnvironment && !filterIamKeyStatus}
            style={{ borderColor: '#6a1b9a', color: '#6a1b9a', textTransform: 'none' }}
          >
            Clear Filters
          </Button>
        </div>

        <div style={{ marginBottom: 24, padding: 16, backgroundColor: '#f8f9fa', borderRadius: 8, border: '1px solid #e9ecef' }}>
          <Typography variant="subtitle1" style={{ marginBottom: 12, color: '#6a1b9a', fontWeight: 600 }}>Select Key Type:</Typography>
          <RadioGroup row value={tableType} onChange={(e) => setTableType(e.target.value)} style={{ gap: 24 }}>
            <FormControlLabel value="ESO" control={<Radio color="primary" />} label="ESO Keys" style={{ color: tableType === 'ESO' ? '#6a1b9a' : '#666' }} />
            <FormControlLabel value="IAM" control={<Radio color="primary" />} label="IAM Users" style={{ color: tableType === 'IAM' ? '#6a1b9a' : '#666' }} />
          </RadioGroup>
        </div>

        {tableType === 'ESO' && (
          <Grid container spacing={2} style={{ marginBottom: 24 }}>
            <Grid item xs={12} md={3}>
              <StyledFormControl variant="outlined" margin="dense">
                <InputLabel>Application Name</InputLabel>
                <StyledSelect value={filterApplicationName} onChange={(e) => setFilterApplicationName(e.target.value)} label="Application Name">
                  <MenuItem value=""><em>All Application Names</em></MenuItem>
                  {getUniqueHistoryApplicationNames().map((applicationName) => (
                    <MenuItem key={applicationName} value={applicationName}>{applicationName}</MenuItem>
                  ))}
                </StyledSelect>
              </StyledFormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <StyledFormControl variant="outlined" margin="dense">
                <InputLabel>Environment</InputLabel>
                <StyledSelect value={filterEnvironment} onChange={(e) => setFilterEnvironment(e.target.value)} label="Environment">
                  <MenuItem value=""><em>All Environments</em></MenuItem>
                  {getUniqueHistoryEnvironments().map((env) => (
                    <MenuItem key={env} value={env}>{env}</MenuItem>
                  ))}
                </StyledSelect>
              </StyledFormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <StyledFormControl variant="outlined" margin="dense">
                <InputLabel>Key Status</InputLabel>
                <StyledSelect value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} label="Key Status">
                  <MenuItem value=""><em>All Key Statuses</em></MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Expiring Soon">Expiring Soon</MenuItem>
                  <MenuItem value="Expired">Expired</MenuItem>
                </StyledSelect>
              </StyledFormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <StyledFormControl variant="outlined" margin="dense">
                <InputLabel>Rotation Status</InputLabel>
                <StyledSelect value={filterRotationStatus} onChange={(e) => setFilterRotationStatus(e.target.value)} label="Rotation Status">
                  <MenuItem value=""><em>All Rotation Statuses</em></MenuItem>
                  <MenuItem value="Success">Success</MenuItem>
                  <MenuItem value="Failed">Failed</MenuItem>
                </StyledSelect>
              </StyledFormControl>
            </Grid>
          </Grid>
        )}

        {tableType === 'IAM' && (
          <Grid container spacing={2} style={{ marginBottom: 24 }}>
            <Grid item xs={12} md={4}>
              <StyledFormControl variant="outlined" margin="dense">
                <InputLabel>AWS Account ID</InputLabel>
                <StyledSelect value={filterIamAwsAccountId} onChange={(e) => setFilterIamAwsAccountId(e.target.value)} label="AWS Account ID">
                  <MenuItem value=""><em>All AWS Accounts</em></MenuItem>
                  {getUniqueIamAwsAccountIds().map((id) => (
                    <MenuItem key={id} value={id}>{id}</MenuItem>
                  ))}
                </StyledSelect>
              </StyledFormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledFormControl variant="outlined" margin="dense">
                <InputLabel>Environment</InputLabel>
                <StyledSelect value={filterIamEnvironment} onChange={(e) => setFilterIamEnvironment(e.target.value)} label="Environment">
                  <MenuItem value=""><em>All Environments</em></MenuItem>
                  {getUniqueIamEnvironments().map((env) => (
                    <MenuItem key={env} value={env}>{env}</MenuItem>
                  ))}
                </StyledSelect>
              </StyledFormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledFormControl variant="outlined" margin="dense">
                <InputLabel>Key Status</InputLabel>
                <StyledSelect value={filterIamKeyStatus} onChange={(e) => setFilterIamKeyStatus(e.target.value)} label="Key Status">
                  <MenuItem value=""><em>All Key Statuses</em></MenuItem>
                  {getUniqueIamKeyStatuses().map((status) => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </StyledSelect>
              </StyledFormControl>
            </Grid>
          </Grid>
        )}

        <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', backgroundColor: '#fff', marginBottom: '16px' }}>
          <TableContainer className={classes.tableContainer} style={{ paddingTop: 0, marginTop: 0 }}>
            {esoKeysLoading ? (
              <Box className={classes.loadingContainer}><CircularProgress /></Box>
            ) : (tableType === 'ESO' ? filteredHistoryData.length : filteredIamData.length) === 0 ? (
              <Box style={{ textAlign: 'center', padding: '48px 32px', color: '#666', backgroundColor: '#f8f9fa' }}>
                <SecurityIcon style={{ fontSize: 64, marginBottom: 16, color: '#ccc' }} />
                <Typography variant="h6" style={{ marginBottom: 8, color: '#666' }}>
                  {tableType === 'ESO' ? 'No ESO Keys Found' : 'No IAM Users Found'}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                  {tableType === 'ESO'
                    ? (historyData.length === 0 ? 'No ESO keys are currently available.' : 'No ESO keys match the selected filters.')
                    : 'No IAM users are currently available.'}
                </Typography>
                {tableType === 'ESO' && historyData.length > 0 && (
                  <Typography variant="body2" style={{ color: '#6a1b9a', fontWeight: '500' }}>
                    Showing 0 of {historyData.length} total keys
                  </Typography>
                )}
              </Box>
            ) : (
              <Table stickyHeader style={{ marginTop: 0 }}>
                <TableHead>
                  <TableRow style={{ backgroundColor: '#f8f9fa' }}>
                    {(tableType === 'ESO' ? tableHeaders : iamTableHeaders).map((header, index) => (
                      <TableCell key={header} style={index === (tableType === 'ESO' ? tableHeaders : iamTableHeaders).length - 1 ? lastHeaderCellStyle : headerCellStyle}>
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(tableType === 'ESO' ? paginatedData : filteredIamData).map((row) => (
                    <React.Fragment key={row.id}>
                      <HoverTableRow>
                        {tableType === 'ESO' ? (
                          <React.Fragment>
                            <TableCell style={{ width: '120px', maxWidth: '120px' }}>{row.application}</TableCell>
                            <TableCell style={{ maxWidth: '250px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: '8px' }} title={row.applicationName}>{row.applicationName}</TableCell>
                            <TableCell style={{ width: '100px' }}>{row.environment}</TableCell>
                            <TableCell style={{ width: '130px' }}>
                              {formatDateTime(row.rotatedOn)}
                            </TableCell>
                            <TableCell style={{ width: '100px' }}>
                              <Chip label={row.keyStatus} className={getStatusChipClass(row.keyStatus)} size="small" />
                            </TableCell>
                            <TableCell style={{ width: '110px' }}>
                              <Chip label={row.rotationStatus} className={getRotationStatusChipClass(row.rotationStatus)} size="small" />
                            </TableCell>
                            <TableCell style={{ width: '60px', textAlign: 'center' }}>
                              <IconButton size="small" onClick={() => toggleRowExpansion(row.id)} style={{ color: '#6a1b9a' }}>
                                {expandedRows.has(row.id) ? <ExpandLess /> : <ExpandMore />}
                              </IconButton>
                            </TableCell>
                          </React.Fragment>
                        ) : (
                          <React.Fragment>
                            <TableCell style={{ fontFamily: 'Monaco, Consolas, monospace', fontSize: '0.85rem', maxWidth: '200px' }}>{row.iamUserName}</TableCell>
                            <TableCell style={{ fontFamily: 'Monaco, Consolas, monospace', fontSize: '0.85rem' }}>{row.awsAccountId}</TableCell>
                            <TableCell style={{ width: '100px' }}>{row.environment}</TableCell>
                            <TableCell style={{ fontFamily: 'Monaco, Consolas, monospace', fontSize: '0.8rem', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.secretName}>{row.secretName}</TableCell>
                            <TableCell style={{ width: '100px' }}>
                              <Chip label={row.keyStatus} className={getStatusChipClass(row.keyStatus)} size="small" />
                            </TableCell>
                            <TableCell style={{ width: '110px' }}>
                              <Chip label={row.rotationType} size="small" style={{ backgroundColor: row.rotationType === 'Automated' ? '#e8f5e8' : '#fff3cd', color: row.rotationType === 'Automated' ? '#2e7d32' : '#856404' }} />
                            </TableCell>
                            <TableCell style={{ width: '130px' }}>{row.lastRotatedDate}</TableCell>
                            <TableCell style={{ width: '60px', textAlign: 'center' }}>
                              <IconButton size="small" onClick={() => toggleRowExpansion(row.id)} style={{ color: '#6a1b9a' }}>
                                {expandedRows.has(row.id) ? <ExpandLess /> : <ExpandMore />}
                              </IconButton>
                            </TableCell>
                          </React.Fragment>
                        )}
                      </HoverTableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0, borderBottom: 'none' }} colSpan={tableType === 'ESO' ? 7 : 8}>
                          <Collapse in={expandedRows.has(row.id)} timeout="auto" unmountOnExit>
                            <Box style={{ margin: '8px 0', padding: '16px 20px', backgroundColor: '#ffffff', border: '1px solid #e3f2fd', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', borderLeft: '4px solid #6a1b9a' }}>
                              <Typography variant="subtitle2" style={{ color: '#6a1b9a', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
                                <InfoIcon style={{ fontSize: '18px', marginRight: '6px' }} />
                                Additional Details
                              </Typography>
                              {tableType === 'ESO' ? (
                                <Grid container spacing={3}>
                                  <Grid item xs={12} sm={6} lg={3}><DetailField label="Client ID" value={row.clientId} monospace /></Grid>
                                  <Grid item xs={12} sm={6} lg={3}><DetailField label="Resource ID" value={row.resourceId} monospace /></Grid>
                                  <Grid item xs={12} sm={6} lg={3}><DetailField label="Rotated By" value={row.rotatedBy} /></Grid>
                                  <Grid item xs={12} sm={6} lg={3}><DetailField label="Expiration Date" value={formatDateTime(row.expirationDate)} /></Grid>
                                  <Grid item xs={12} sm={6}><DetailField label="Notes" value={row.notes} /></Grid>
                                  <Grid item xs={12}><DetailField label="API Endpoint" value={row.apiUrl} monospace highlighted /></Grid>
                                </Grid>
                              ) : (
                                <Grid container spacing={3}>
                                  <Grid item xs={12} sm={6} lg={3}><DetailField label="Access Key ID" value={row.accessKeyId} monospace /></Grid>
                                  <Grid item xs={12} sm={6} lg={2}><DetailField label="Created Date" value={row.createdDate} /></Grid>
                                  <Grid item xs={12} sm={6} lg={2}><DetailField label="Rotated By" value={row.rotatedBy} /></Grid>
                                  <Grid item xs={12} sm={6} lg={5}><DetailField label="Notes" value={row.notes} /></Grid>
                                </Grid>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>

          {!esoKeysLoading && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={tableType === 'ESO' ? filteredHistoryData.length : filteredIamData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </div>
      </div>

      <Snackbar open={!!successMessage} autoHideDuration={6000} onClose={() => setSuccessMessage('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSuccessMessage('')} severity="success">{successMessage}</Alert>
      </Snackbar>

      <Snackbar open={!!errorMessage} autoHideDuration={8000} onClose={() => setErrorMessage('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setErrorMessage('')} severity="error">{errorMessage}</Alert>
      </Snackbar>
    </div>
  );
};

export default connect()(withRouter(withStyles(styles)(KeyManagement)));